import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, ExternalLink, RefreshCw, Search, Smartphone } from 'lucide-react';

import type {
  IosDeviceRegistration,
  IosDeviceRegistrationConfig,
  Page,
} from '@/api/types';
import { PageHeader } from '@/components/AppShell';
import { DataTable, PageSizeSelect, Pagination, type Column } from '@/components/DataTable';
import { useToast } from '@/components/Toast';
import { Badge, Button, EmptyState, ErrorState, IconButton, TableSkeleton, TextInput } from '@/components/ui';
import { dateTime } from '@/lib/format';
import { useDebounced, useResource } from '@/lib/hooks';
import { usePageSize } from '@/lib/pageSize';

export function IosDevicesPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);
  const [pageSize, setPageSize] = usePageSize('ios-devices');

  const config = useResource<IosDeviceRegistrationConfig>(
    '/api/v1/admin/ios-device-registrations/config',
    undefined,
    [],
  );
  const devices = useResource<Page<IosDeviceRegistration>>(
    '/api/v1/admin/ios-device-registrations',
    { search: debouncedSearch, page, page_size: pageSize },
    [debouncedSearch, page, pageSize],
  );

  const language = i18n.language;
  const registrationUrl = config.data
    ? `${config.data.registration_url}${language.startsWith('en') ? '?locale=en' : ''}`
    : '';

  async function copy(value: string, message: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      // Both the fixed link and every UDID remain selectable on screen.
    }
  }

  const columns: Column<IosDeviceRegistration>[] = [
    {
      key: 'udid',
      header: t('iosDevices.udid'),
      render: (device) => (
        <div className="flex min-w-[18rem] items-center gap-2">
          <code className="break-all rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-xs text-ink">
            {device.udid}
          </code>
          <IconButton
            variant="ghost"
            label={t('iosDevices.copyUdid')}
            icon={<Copy className="h-3.5 w-3.5" />}
            onClick={() => copy(device.udid, t('iosDevices.udidCopied'))}
          />
        </div>
      ),
    },
    {
      key: 'version',
      header: t('iosDevices.iosVersion'),
      render: (device) => device.ios_version ?? <span aria-hidden>—</span>,
    },
    {
      key: 'registered',
      header: t('iosDevices.registeredAt'),
      align: 'right',
      render: (device) => dateTime(device.registered_at, language) ?? '—',
    },
    {
      key: 'lastSeen',
      header: t('iosDevices.lastSeenAt'),
      align: 'right',
      render: (device) => dateTime(device.last_seen_at, language) ?? '—',
    },
  ];

  return (
    <>
      <PageHeader
        title={t('iosDevices.title')}
        subtitle={t('iosDevices.subtitle')}
        actions={
          <Button
            icon={<RefreshCw className="h-4 w-4" />}
            onClick={() => devices.reload()}
          >
            {t('iosDevices.refresh')}
          </Button>
        }
      />

      <section className="card mb-5 p-4" aria-labelledby="ios-registration-link">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 id="ios-registration-link" className="text-sm font-semibold text-ink">
                {t('iosDevices.linkTitle')}
              </h2>
              {config.data ? (
                <Badge tone={config.data.enabled ? 'positive' : 'caution'}>
                  {t(config.data.enabled ? 'iosDevices.enabled' : 'iosDevices.disabled')}
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 max-w-2xl text-sm text-ink-subtle">
              {t('iosDevices.linkBody')}
            </p>
          </div>
          {registrationUrl ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                disabled={!config.data?.enabled}
                icon={<Copy className="h-4 w-4" />}
                onClick={() => copy(registrationUrl, t('iosDevices.linkCopied'))}
              >
                {t('iosDevices.copyLink')}
              </Button>
              <Button
                disabled={!config.data?.enabled}
                icon={<ExternalLink className="h-4 w-4" />}
                onClick={() => window.open(registrationUrl, '_blank', 'noopener,noreferrer')}
              >
                {t('iosDevices.openLink')}
              </Button>
            </div>
          ) : null}
        </div>
        {config.error ? (
          <div className="mt-4">
            <ErrorState message={t('common.loadFailed')} onRetry={config.reload} />
          </div>
        ) : registrationUrl ? (
          <code className="mt-4 block break-all rounded-lg bg-surface-sunken px-3 py-2 font-mono text-xs text-ink-muted">
            {registrationUrl}
          </code>
        ) : (
          <div className="mt-4 h-9 animate-pulse rounded-lg bg-surface-sunken" />
        )}
      </section>

      <div className="card">
        <div className="flex flex-wrap items-center gap-3 border-b border-line p-3">
          <div className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
            />
            <TextInput
              type="search"
              aria-label={t('common.search')}
              placeholder={t('iosDevices.searchPlaceholder')}
              className="pl-9"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <PageSizeSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </div>

        {devices.error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={devices.reload} />
        ) : devices.loading && !devices.data ? (
          <TableSkeleton columns={4} />
        ) : devices.data && devices.data.items.length === 0 ? (
          <EmptyState
            icon={<Smartphone className="h-8 w-8" />}
            title={t('iosDevices.emptyTitle')}
            body={t('iosDevices.emptyBody')}
          />
        ) : (
          <>
            <DataTable
              caption={t('iosDevices.title')}
              columns={columns}
              rows={devices.data?.items ?? []}
              rowKey={(row) => row.id}
            />
            <Pagination
              page={page}
              pageSize={pageSize}
              total={devices.data?.total ?? 0}
              onPage={setPage}
            />
          </>
        )}
      </div>
    </>
  );
}
